/**
 * ČÚZK DMVS / DTM — Technická infrastruktura (public WMS overlay).
 * GetCapabilities: https://dmvs.cuzk.gov.cz/api/wms/dtm_ti_ver
 *
 * MVP: raster WMS visualization only. Authorized JVF/GeoPackage vector export is Phase 2.
 * Overlays are shown on Obecný and Stavba drawings (not Montáž / Záfuk / Optika / …).
 */

import { DRAWING_IDS } from './drawing.constants';

/** Public WMS endpoint (GetMap / GetCapabilities). */
export const DTM_TI_WMS_URL = 'https://dmvs.cuzk.gov.cz/api/wms/dtm_ti_ver';

/** Prod-relative proxy path (external nginx → dmvs.cuzk.gov.cz). */
export const DTM_TI_WMS_PROXY_PATH = '/wms-dmvs/dtm_ti_ver';

export const DTM_TI_WMS_VERSION = '1.3.0';
export const DTM_TI_WMS_FORMAT = 'image/png';
export const DTM_TI_WMS_ATTRIBUTION = '© ČÚZK – DMVS TI';

/**
 * Stable app ids for toggles / localStorage (not necessarily equal to WMS layer Name).
 */
export type DtmTiNetworkId = 'voda' | 'plyn' | 'elektrina' | 'optika';

export interface DtmTiNetworkLayerDef {
  id: DtmTiNetworkId;
  /** Czech UI label */
  label: string;
  /** WMS Layer Name from GetCapabilities */
  wmsLayer: string;
  /** Short hint for layer menu */
  description?: string;
}

/**
 * Product MVP network toggles → DTM TI WMS layer names.
 * - voda → `voda` (Vodovod)
 * - plyn → `plyn` (Plynovod)
 * - elektrina → `el_ved` (Elektrické vedení)
 * - optika → `el_kom` (Elektronická komunikace / telekomunikace)
 */
export const DTM_TI_NETWORK_LAYERS: readonly DtmTiNetworkLayerDef[] = [
  { id: 'voda', label: 'Voda', wmsLayer: 'voda', description: 'Vodovod (DMVS TI)' },
  { id: 'plyn', label: 'Plyn', wmsLayer: 'plyn', description: 'Plynovod (DMVS TI)' },
  {
    id: 'elektrina',
    label: 'Elektřina',
    wmsLayer: 'el_ved',
    description: 'Elektrické vedení (DMVS TI)',
  },
  {
    id: 'optika',
    label: 'Optika',
    wmsLayer: 'el_kom',
    description: 'Elektronická komunikace / telekomunikace (DMVS TI)',
  },
] as const;

export const DTM_TI_NETWORK_IDS: readonly DtmTiNetworkId[] = DTM_TI_NETWORK_LAYERS.map((l) => l.id);

/**
 * Drawing ids where DMVS TI WMS overlays may appear (Obecný + Stavba).
 * Prefs stay global; map sync / layer-menu toggles apply only here.
 */
export const DTM_TI_VISIBLE_ON_DRAWING_IDS = [
  DRAWING_IDS.GENERAL,
  DRAWING_IDS.STAVBA,
] as const;

/** True when the active map drawing should show / sync DMVS TI overlays. */
export function isDtmTiVisibleOnDrawing(drawingId: string | null | undefined): boolean {
  const id = drawingId ?? '';
  return (DTM_TI_VISIBLE_ON_DRAWING_IDS as readonly string[]).includes(id);
}

/** Default: all MVP networks visible until the user changes the preference. */
export function defaultEnabledDtmTiNetworkIds(): readonly DtmTiNetworkId[] {
  return DTM_TI_NETWORK_IDS;
}

export function getDtmTiNetworkById(id: string): DtmTiNetworkLayerDef | undefined {
  return DTM_TI_NETWORK_LAYERS.find((l) => l.id === id);
}

/**
 * DMVS TI WMS layers used to screen Křížení sítí along the route.
 * Only a GetFeatureInfo hit becomes a proposal — never a catalog dump.
 */
export interface DtmTiScreeningLayerDef {
  wmsLayer: string;
  subjectKey: string;
  labelCs: string;
}

export const DTM_TI_SCREENING_LAYERS: readonly DtmTiScreeningLayerDef[] = [
  { wmsLayer: 'voda', subjectKey: 'vodovod', labelCs: 'Vodovod (DTM)' },
  { wmsLayer: 'priv_vody', subjectKey: 'vodovod', labelCs: 'Přívod vody (DTM)' },
  { wmsLayer: 'plyn', subjectKey: 'plyn', labelCs: 'Plynovod (DTM)' },
  { wmsLayer: 'el_ved', subjectKey: 'elektrina', labelCs: 'Elektřina (DTM)' },
  { wmsLayer: 'el_kom', subjectKey: 'elektronicke_komunikace', labelCs: 'Elektronické komunikace (DTM)' },
  { wmsLayer: 'kan', subjectKey: 'kanalizace', labelCs: 'Kanalizace (DTM)' },
  { wmsLayer: 'teplo', subjectKey: 'teplo', labelCs: 'Teplo (DTM)' },
  { wmsLayer: 'produkt', subjectKey: 'produktovody', labelCs: 'Produktovody (DTM)' },
  { wmsLayer: 'zar_dopr_st', subjectKey: 'telematika', labelCs: 'Telematika (DTM)' },
  { wmsLayer: 'zar_st_ti', subjectKey: 'obecni_site', labelCs: 'Obecní TI (DTM)' },
  { wmsLayer: 'sdil_st_ti', subjectKey: 'obecni_site', labelCs: 'Sdílená TI (DTM)' },
] as const;
